import styled from '@emotion/styled';
import type { AxiosError, AxiosResponse } from 'axios';
import { useState } from 'react';
import { useMutation } from 'react-query';

import { Button, EmailInput, Field, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import { useCurrentUser } from '@hotjar/user';
import { verifyEmail } from '@hotjar/webapp/domains/common/verify-email/api';
import type {
  VerifyEmailErrorReasons,
  VerifyEmailErrorResponseDto,
  VerifyEmailSuccessResponseDto,
} from '@hotjar/webapp/domains/common/verify-email/model';
import { useAnonymousId } from '@hotjar/webapp/hooks/useAnonymousId';
import { ERROR_MESSAGES } from './constants';

const EmailField = styled(Field)`
  margin-bottom: ${tokens.spacer.x2};
`;

interface EmailFormProps {
  onSuccess: ({ verificationId, email }: { verificationId: string; email: string }) => void;
}

export function EmailForm({ onSuccess }: EmailFormProps): JSX.Element {
  const { t } = useTranslation('profile');

  const [emailVerificationErrors, setEmailVerificationErrors] = useState<
    Array<VerifyEmailErrorReasons>
  >([]);

  const { data: currentUser } = useCurrentUser();
  const { anonymousId } = useAnonymousId();

  const verifyEmailMutation = useMutation(verifyEmail);

  const handleEmailFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setEmailVerificationErrors([]);

    if (!currentUser || anonymousId === undefined) {
      return;
    }

    const data = new FormData(event.currentTarget);
    const email = String(data.get('email'));

    const metadata = { email, name: currentUser?.name, verifyWithCode: true, uuid: anonymousId };

    try {
      const response = await verifyEmailMutation.mutateAsync({
        email,
        action: 'change_email',
        verifyEmailMetadata: metadata,
      });

      const verificationId = (response as AxiosResponse<VerifyEmailSuccessResponseDto>).data?.vid;

      onSuccess({ verificationId, email });
    } catch (error) {
      const err = error as AxiosError<VerifyEmailErrorResponseDto> &
        AxiosError<{ message: VerifyEmailErrorReasons }>;

      if (err.response?.data.reasons) {
        setEmailVerificationErrors(err.response?.data.reasons);
      } else if (err.response?.data.message) {
        setEmailVerificationErrors([err.response?.data.message]);
      } else {
        setEmailVerificationErrors(['unhandled']);
      }
    }
  };

  return (
    <form onSubmit={handleEmailFormSubmit}>
      <EmailField
        inputId="change-email"
        labelText={t('account-email.form.label')}
        isInvalid={emailVerificationErrors.length > 0}
        invalidText={t(ERROR_MESSAGES[emailVerificationErrors[0]])}
        size="l"
        isRequired
        helperText={t('account-email.form.helper-text')}
      >
        <EmailInput name="email" isRequired id="change-email" />
      </EmailField>

      <Button
        isLoading={verifyEmailMutation.isLoading}
        loadingText={t('account-email.form.button-loading-state')}
        type="submit"
      >
        {t('account-email.form.button-save')}
      </Button>
    </form>
  );
}
